import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { apiUrl } from '../utils/environment';

export const apiSlice = createApi({
    reducerPath: 'api',

    baseQuery: fetchBaseQuery({
        baseUrl: apiUrl,

        prepareHeaders: (headers, { getState }) => {
            headers.set('accept', 'application/json');
            const token = getState().auth.token;
            token && headers.set('authorization', `Bearer ${token}`);
            return headers;
        },
        // responseHandler: (response) => {
        //     // eslint-disable-next-line no-console
        //     console.log('Unauthorized1', response);
        //     return response;
        // },
    }),
    endpoints: (builder) => ({
        // LIST DATA
        getData: builder.query({
            query: ({ list, params }) => ({
                url: `${list}`,
                params,
            }),
        }),
        getListData: builder.query({
            query: ({ list, params }) => ({
                url: `${list}`,
                params,
            }),
        }),
        getListDataWithPost: builder.mutation({
            query: ({ list, body, params }) => ({
                url: `${list}`,
                method: 'POST',
                body,
                params,
            }),
        }),
        getListDataWithGet: builder.mutation({
            query: ({ list, body, params }) => ({
                url: `${list}`,
                method: 'GET',
                body,
                params,
            }),
        }),
        createListData: builder.mutation({
            query: ({ list, body, params, headers }) => ({
                url: `${list}`,
                method: 'POST',
                body,
                params,
                headers,
            }),
        }),
        updateListData: builder.mutation({
            query: ({ list, body, params }) => ({
                url: `${list}`,
                method: 'PUT',
                body,
                params,
            }),
        }),
        deleteListData: builder.mutation({
            query: ({ list }) => ({
                url: `${list}`,
                method: 'DELETE',
            }),
        }),
        // AUTHENTICATION
        registerGuestUser: builder.mutation({
            query: (user) => ({
                url: '/guest/register',
                method: 'POST',
                body: user,
            }),
        }),
        loginUser: builder.mutation({
            query: (user) => ({
                url: '/login',
                method: 'POST',
                body: user,
            }),
        }),
        registerMinister: builder.mutation({
            query: (formData) => ({
                url: '/pastors',
                method: 'POST',
                body: formData,
            }),
        }),
        // ACTIONS
        loadChurchIdPdf: builder.query({
            query: ({ list, params }) => ({
                url: `${list}`,
                params,
                responseHandler: (res) => res.blob(),
            }),
        }),
        bookAppointment: builder.mutation({
            query: ({ id, date_time }) => ({
                url: `/appointments/${id}/book`,
                method: 'POST',
                body: { seats: 1, date_time },
            }),
        }),
        recordAttendance: builder.mutation({
            query: ({ service, member }, temperature) => ({
                url: `/attendance/services/${service}/member/${member}`,
                method: 'POST',
                body: { temperature },
            }),
        }),
        deleteBooking: builder.mutation({
            query: ({ id }) => ({
                url: `/bookings/${id}`,
                method: 'DELETE',
            }),
        }),
        loadChurchInfo: builder.query({
            query: (id) => `/churches/${id}`,
        }),
        loadUserLikes: builder.query({
            query: (id) => `/posts/${id}/likes`,
        }),
        sendContribution: builder.mutation({
            query: ({ amount, phone, reason }) => ({
                url: '/contribution',
                method: 'POST',
                body: { amount, phone, reason },
            }),
        }),
        getContributionInfo: builder.query({
            query: () => '/contribution',
        }),
        loadDevotionInfo: builder.query({
            query: (id) => `/devotions/${id}`,
        }),
        toggleDevotionLike: builder.mutation({
            query: (id) => ({
                url: `/devotions/${id}/toggle_like`,
                method: 'POST',
            }),
        }),
        setEventReminder: builder.mutation({
            query: (id) => ({
                url: `/events/${id}`,
                method: 'PUT',
            }),
        }),
        // FEED
        getFeedInfo: builder.query({
            query: (id) => `/posts/${id}`,
        }),
        reportFeed: builder.mutation({
            query: ({ id, reasons, message }) => ({
                url: `/posts/${id}/report`,
                method: 'POST',
                body: { reasons, message },
            }),
        }),
        createFeed: builder.mutation({
            query: ({ post, images, publicPost }) => {
                const formData = new FormData();
                formData.append('post', post);
                publicPost && formData.append('audience', 'Public');
                images.forEach((img) => {
                    formData.append('images[]', img);
                });
                return {
                    url: '/posts',
                    method: 'POST',
                    body: formData,
                };
            },
        }),
        deleteFeed: builder.mutation({
            query: (id) => ({
                url: `/posts/${id}`,
                method: 'DELETE',
            }),
        }),
        loadFeedComments: builder.query({
            query: (id) => `/posts/${id}/comments`,
        }),
        createCommet: builder.mutation({
            query: ({ id, comment }) => ({
                url: `/posts/${id}/comments`,
                method: 'POST',
                body: { comment },
            }),
        }),
        deleteComment: builder.mutation({
            query: ({ commentId, postId }) => ({
                url: `/posts/${postId}/comments/${commentId}`,
                method: 'DELETE',
            }),
        }),
        createHelp: builder.mutation({
            query: ({ title, message, priority }) => ({
                url: '/contact',
                method: 'POST',
                body: { title, message, priority },
            }),
        }),
        //
        messageChurch: builder.mutation({
            query: ({ title, message }) => ({
                url: '/message',
                method: 'POST',
                body: { title, message },
            }),
        }),
    }),
});

export const {
    useRegisterGuestUserMutation,
    useLoginUserMutation,
    useRegisterMinisterMutation,
    useGetDataQuery,
    useGetListDataQuery,
    useGetListDataWithPostMutation,
    useGetListDataWithGetMutation,
    useCreateListDataMutation,
    useUpdateListDataMutation,
    useDeleteListDataMutation,
    useLoadChurchIdPdfQuery,
    useBookAppointmentMutation,
    useRecordAttendanceMutation,
    useDeleteBookingMutation,
    useLoadChurchInfoQuery,
    useLoadUserLikesQuery,
    useSendContributionMutation,
    useGetContributionInfoQuery,
    useLoadDevotionInfoQuery,
    useToggleDevotionLikeMutation,
    useSetEventReminderMutation,
    useGetFeedInfoQuery,
    useReportFeedMutation,
    useCreateFeedMutation,
    useDeleteFeedMutation,
    useLoadFeedCommentsQuery,
    useCreateCommetMutation,
    useDeleteCommentMutation,
    useCreateHelpMutation,
    useMessageChurchMutation,
} = apiSlice;
